import { ChevronDown, ChevronRight } from "lucide-react";
import { useState } from "react";
import type { UseFormRegister } from "react-hook-form";
import { Section } from "../view/section";
import { FormTextarea } from "./form-field";
import type { InsuranceFormData } from "./types";

interface Props {
  register: UseFormRegister<InsuranceFormData>;
  values: Partial<InsuranceFormData>;
}

function CollapsibleField({
  label,
  id,
  preview,
  children,
}: {
  label: string;
  id: string;
  preview: string | null | undefined;
  children: React.ReactNode;
}): React.ReactElement {
  const [open, setOpen] = useState(!!preview);

  return (
    <div className="border rounded-md">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 w-full px-3 py-2 text-sm font-medium text-left hover:bg-accent/50 transition-colors"
        data-testid={`collapse-${id}`}
      >
        {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
        {label}
        {!open && preview && (
          <span className="ml-2 text-muted-foreground font-normal truncate max-w-md">
            {preview.substring(0, 80)}
            {preview.length > 80 ? "..." : ""}
          </span>
        )}
      </button>
      {open && <div className="px-3 pb-3">{children}</div>}
    </div>
  );
}

const fields: { key: keyof InsuranceFormData; label: string }[] = [
  { key: "assetDescription", label: "Asset Description" },
  { key: "construction", label: "Construction" },
  { key: "recreationalFeatures", label: "Recreational Features" },
  { key: "retainingWalls", label: "Retaining Walls" },
  { key: "specialFeatures", label: "Special Features" },
  { key: "features", label: "Features" },
  { key: "detailedDescription", label: "Detailed Description of Improvements" },
];

export function SectionDescriptionForm({ register, values }: Props): React.ReactElement {
  return (
    <Section id="description" title="Property Description">
      <div className="space-y-1">
        {fields.map(({ key, label }) => (
          <CollapsibleField key={key} label={label} id={key} preview={values[key] as string | null}>
            <FormTextarea id={key} rows={4} placeholder={`Enter ${label.toLowerCase()}...`} {...register(key)} />
          </CollapsibleField>
        ))}
      </div>
    </Section>
  );
}
